import { useEffect, useState } from 'react'
import {
  ClonedDatasetResponse,
  Dataset,
  GetDatasetVersionsResponse,
  UpdateDatasetResponse,
} from 'tabs/data/api/generated'
import { cloneDatasetVersion, create, getDatasets, patchDataset } from 'tabs/data/api/datasets'

export const useDatasets = () => {
  const [data, setData] = useState<GetDatasetVersionsResponse['datasets'] | []>([])
  const [activeDataset, setActiveDataset] = useState<Dataset | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | string | null>(null)

  const fetchDatasetVersions = async () => {
    setLoading(true)
    setError(null)

    try {
      const jsonData = await getDatasets()

      setData(jsonData.datasets || [])
    } catch (err: unknown) {
      setError((err as Error)?.message || 'An unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  const updateDataset = async (datasetId, params): Promise<UpdateDatasetResponse> => {
    try {
      setLoading(true)
      return patchDataset(datasetId, params)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createDataset = async (params): Promise<void> => {
    try {
      setLoading(true)
      return create(params)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  const cloneDataset = async (datasetId, versionLabel): Promise<ClonedDatasetResponse> => {
    try {
      setLoading(true)
      return cloneDatasetVersion(datasetId, versionLabel)
    } catch (error) {
      throw error
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDatasetVersions()
  }, [])

  useEffect(() => {
    if (activeDataset && data[0]) {
      setActiveDataset(activeDataset)
    }
  }, [data])

  return {
    data,
    activeDataset,
    setActiveDataset,
    loading,
    refetch: fetchDatasetVersions,
    updateDataset,
    cloneDataset,
    createDataset,
    error,
  }
}
